import React, { useState, useRef, useEffect, useMemo } from 'react';
import { getTonalColors, isHexColor } from '../../../utils/calendar/constants';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Plus, ChevronDown, MoreVertical, LogOut,
  CheckSquare, Square, Settings, Check, MessageSquare, Share2,
  Search, Tag, Calendar, ListChecks, X, ChevronLeft,
  Circle, CheckCircle2, Pencil, HelpCircle, ChevronsLeft, ChevronsRight,
} from 'lucide-react';
import SpaceMembers from '../../spaces/SpaceMembers';
import { Logo, MosaicMark } from '../../ui/Logo';
import { CalendarEvent } from '../../../hooks/useSupabaseEvents';
import { COLORS } from '../../../utils/calendar/constants';
import { getUnassignedEvents, resolveCalendar, UNASSIGNED_CALENDAR_ID } from '../../../utils/calendar/eventHelpers';
import { useSpace } from '../../../contexts/SpaceContext';
import { useAuth } from '../../../contexts/AuthContext';
import { Todo } from '../../../hooks/useSupabaseKanban';
import { useCalendar } from '../../../contexts/CalendarContext';
import { Calendar as CalendarType } from '../../../types';
import { Tag as ContentTag } from '../../../hooks/useTags';
import { useDateFormat, formatShortDate, DateFormat } from '../../settings/TimePicker';

interface ImportedCalendar {
  id: string;
  name: string;
  url: string;
  color: string;
  enabled: boolean;
  sync_error?: string | null;
}

interface KanbanColumn {
  id: string;
  name: string;
  is_done_column?: boolean;
}

interface CalendarSidebarProps {
  // Layout
  collapsed: boolean;
  onToggleCollapse: () => void;

  // Data
  events: CalendarEvent[];
  filteredEvents: CalendarEvent[];
  importedCalendars: ImportedCalendar[];
  importedEvents: { calendar_id: string }[];
  currentYear: number;

  // Todos
  todos: Todo[];
  kanbanColumns: KanbanColumn[];
  onOpenTodo: (todoId: string) => void;
  onAddTodo: () => void;
  onQuickAddTodo?: (title: string) => Promise<void>;

  // Tag filters
  selectedCalendarFilters: Set<string>;
  onToggleCalendarFilter: (tagId: string) => void;
  onSelectAllTags: () => void;
  onDeselectAllTags: () => void;

  // Calendar management
  onToggleCalendar: (calendarId: string) => void;
  onUpdateCalendar: (calendarId: string, updates: { enabled?: boolean }) => Promise<void>;
  onSyncCalendar: (calendarId: string) => void;
  syncing: string | null;

  // Quick menus
  onOpenCategoryMenu: (tag: { id?: string; name: string; color: string; is_shared?: boolean; share_token?: string | null }, anchor: HTMLButtonElement, isNew: boolean) => void;
  onOpenCalendarMenu: (calendar: { id: string; name: string; url: string; color: string }, anchor: HTMLButtonElement) => void;

  // Actions
  onShowAddCalendarModal: () => void;
  onOpenSpaceSettings: () => void;
  onSignOut: () => Promise<void>;

  // Search
  searchQuery: string;
  onSearchChange: (query: string) => void;

  // Content tag filter
  contentTags?: ContentTag[];
  activeContentTagFilter?: ContentTag | null;
  onContentTagFilterChange?: (tag: ContentTag | null) => void;
}

const getTodayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getTomorrowKey = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatTaskDate = (dateKey: string, dateFormat: DateFormat): string => {
  const today = getTodayKey();
  const tomorrow = getTomorrowKey();
  if (dateKey === today) return 'Today';
  if (dateKey === tomorrow) return 'Tomorrow';
  const d = new Date(dateKey + 'T00:00:00');
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
  return `${weekday}, ${formatShortDate(dateKey, dateFormat)}`;
};

const TASKS_PAGE_SIZE = 12;
const ICON_BTN = "w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[var(--sidebar-hover)] text-[var(--content-tertiary)] hover:text-[var(--content-primary)] transition-colors";

export const CalendarSidebar: React.FC<CalendarSidebarProps> = ({
  collapsed,
  onToggleCollapse,
  events,
  filteredEvents,
  importedCalendars,
  importedEvents,
  currentYear,
  todos,
  kanbanColumns,
  onOpenTodo,
  onAddTodo,
  onQuickAddTodo,
  selectedCalendarFilters,
  onToggleCalendarFilter,
  onSelectAllTags,
  onDeselectAllTags,
  onToggleCalendar,
  onUpdateCalendar,
  onSyncCalendar,
  syncing,
  onOpenCategoryMenu,
  onOpenCalendarMenu,
  onShowAddCalendarModal,
  onOpenSpaceSettings,
  onSignOut,
  searchQuery,
  onSearchChange,
  contentTags = [],
  activeContentTagFilter = null,
  onContentTagFilterChange,
}) => {
  const { calendars, onTodoToggle: onToggleTodo } = useCalendar();
  const { user } = useAuth();
  const { currentSpace, setCurrentSpace, spaces, spacesLoading, isPersonalSpace } = useSpace();
  const { theme } = useTheme();
  const dateFormat = useDateFormat();
  const isDark = theme === 'dark';

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [calendarsCollapsed, setCalendarsCollapsed] = useState(false);
  const [tasksCollapsed, setTasksCollapsed] = useState(false);
  const [mainMenuOpen, setMainMenuOpen] = useState(false);
  const [tasksVisible, setTasksVisible] = useState(TASKS_PAGE_SIZE);
  const [railPopover, setRailPopover] = useState<'categories' | 'calendars' | 'tasks' | 'search' | null>(null);
  const [railSearchQuery, setRailSearchQuery] = useState('');
  const [tagSuggestionsOpen, setTagSuggestionsOpen] = useState(false);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [quickAdding, setQuickAdding] = useState(false);
  const quickAddInputRef = useRef<HTMLInputElement>(null);
  const railPopoverRef = useRef<HTMLDivElement>(null);
  const mainMenuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const railSearchInputRef = useRef<HTMLInputElement>(null);

  // Close rail popover on click outside
  useEffect(() => {
    if (!railPopover) return;
    const handleClick = (e: MouseEvent) => {
      if (railPopoverRef.current && !railPopoverRef.current.contains(e.target as Node)) {
        setRailPopover(null);
        setRailSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [railPopover]);

  // Sync rail search with main search
  useEffect(() => {
    if (railPopover === 'search') {
      setRailSearchQuery(searchQuery);
      setTimeout(() => railSearchInputRef.current?.focus(), 50);
    }
  }, [railPopover]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mainMenuRef.current && !mainMenuRef.current.contains(event.target as Node)) {
        setMainMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const prevTagsRef = useRef<CalendarType[]>(calendars);
  useEffect(() => {
    if (calendars.length > 0) prevTagsRef.current = calendars;
  }, [calendars]);

  const displayTags = calendars.length > 0 ? calendars : prevTagsRef.current;
  const isInTransition = calendars.length === 0 && prevTagsRef.current.length > 0;

  const toggleSection = (section: string) => {
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(section)) newCollapsed.delete(section);
    else newCollapsed.add(section);
    setCollapsedSections(newCollapsed);
  };

  const unassignedEvents = useMemo(() =>
    getUnassignedEvents(events, calendars).filter(e =>
      e.dates.some(d => d.startsWith(`${currentYear}-`))
    ), [events, calendars, currentYear]);

  const hasUnassignedEvents = unassignedEvents.length > 0;

  const effectiveFilters = isInTransition
    ? new Set([...displayTags.map(t => t.id), ...(hasUnassignedEvents ? [UNASSIGNED_CALENDAR_ID] : [])])
    : selectedCalendarFilters;

  const totalSelectableCount = displayTags.length + (hasUnassignedEvents ? 1 : 0);
  const selectedCount = Array.from(effectiveFilters).filter(id =>
    id === UNASSIGNED_CALENDAR_ID ? hasUnassignedEvents : displayTags.some(t => t.id === id)
  ).length;
  const allTagsSelected = selectedCount === totalSelectableCount;
  const noTagsSelected = selectedCount === 0;

  const totalYearEvents = useMemo(() =>
    events.filter(e => e.dates.some(d => d.startsWith(`${currentYear}-`))).length,
    [events, currentYear]);

  const tagEventCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const tag of displayTags) {
      counts[tag.id] = events.filter(e => {
        const resolved = resolveCalendar(e, displayTags);
        return resolved?.id === tag.id
          && e.dates.some(d => d.startsWith(`${currentYear}-`));
      }).length;
    }
    counts[UNASSIGNED_CALENDAR_ID] = unassignedEvents.length;
    return counts;
  }, [events, displayTags, currentYear, unassignedEvents]);

  // Tag suggestions for search bar: contentTags that fuzzy-match the current search query
  const tagSuggestions = useMemo(() => {
    if (!searchQuery.trim() || contentTags.length === 0) return [];
    const q = searchQuery.trim().toLowerCase();
    return contentTags.filter(t => t.name.includes(q)).slice(0, 5);
  }, [searchQuery, contentTags]);

  const doneCol = kanbanColumns.find(c => c.is_done_column);
  const isTaskDone = (t: Todo) => !!(t.completed_at) || !!(doneCol && t.column_id === doneCol.id);

  const getTaskColor = (task: Todo): string | null => {
    if (!task.calendar_id) return null;
    const tag = calendars.find(t => t.id === task.calendar_id);
    if (!tag) return null;
    const tonal = getTonalColors(tag.color);
    if (!tonal) return null;
    return isDark ? tonal.textDark : tonal.textLight;
  };

  // Show today + future tasks, plus past INCOMPLETE tasks; hide past completed tasks
  const sortedTasks = useMemo(() => {
    const today = getTodayKey();
    const active = todos.filter(t => !t.archived_at);
    return active.filter(t => {
      if (!t.due_date) return true;
      if (t.due_date >= today) return true;
      return !isTaskDone(t);
    }).sort((a, b) => {
      if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
      if (a.due_date) return -1;
      if (b.due_date) return 1;
      return 0;
    });
  }, [todos, doneCol]);

  const visibleTasks = sortedTasks.slice(0, tasksVisible);
  const hasMoreTasks = sortedTasks.length > tasksVisible;

  const groupedTasks = useMemo(() => {
    const today = getTodayKey();
    const groups: { label: string; tasks: Todo[] }[] = [];
    const labelMap: Record<string, Todo[]> = {};
    const overdueItems: Todo[] = [];
    for (const t of visibleTasks) {
      if (t.due_date && t.due_date < today) {
        overdueItems.push(t);
      } else {
        const label = t.due_date ? formatTaskDate(t.due_date, dateFormat) : 'Unplanned';
        if (!labelMap[label]) labelMap[label] = [];
        labelMap[label].push(t);
      }
    }
    if (overdueItems.length > 0) {
      groups.push({ label: 'Overdue', tasks: overdueItems });
    }
    const orderedKeys: string[] = [];
    const tomorrow = getTomorrowKey();
    const todayLabel = formatTaskDate(today, dateFormat);
    const tomorrowLabel = formatTaskDate(tomorrow, dateFormat);
    if (labelMap[todayLabel]) orderedKeys.push(todayLabel);
    if (labelMap[tomorrowLabel]) orderedKeys.push(tomorrowLabel);
    for (const k of Object.keys(labelMap)) {
      if (k !== todayLabel && k !== tomorrowLabel && k !== 'Unplanned') orderedKeys.push(k);
    }
    if (labelMap['Unplanned']) orderedKeys.push('Unplanned');
    for (const key of orderedKeys) {
      groups.push({ label: key, tasks: labelMap[key] });
    }
    // Sort each group: done tasks to the bottom
    for (const g of groups) {
      g.tasks.sort((a, b) => {
        const aDone = !!(doneCol && a.column_id === doneCol.id) ? 1 : 0;
        const bDone = !!(doneCol && b.column_id === doneCol.id) ? 1 : 0;
        return aDone - bDone;
      });
    }
    return groups;
  }, [visibleTasks, doneCol, dateFormat]);

  const personalSpaceName = user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'Personal';
  const currentSpaceName = isPersonalSpace ? personalSpaceName : currentSpace?.name || 'Calendar';

  // ─── COLLAPSED ICON RAIL ─────────────────────────────────────────
  const RAIL_POPOVER = "absolute left-[44px] top-0 w-56 bg-[var(--surface-primary)] border border-[var(--border-primary)] rounded-xl overflow-hidden z-50 animate-fadeIn";

  if (collapsed) {
    return (
      <div className="h-full flex flex-col items-center gap-1 flex-shrink-0" style={{ width: '44px', overflow: 'visible' }}>
        {/* Logo mark */}
        <div className="h-10 mb-2 flex items-center flex-shrink-0">
          <button onClick={onToggleCollapse} className={ICON_BTN} title="Expand sidebar">
            <MosaicMark size="sm" />
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <button
            onClick={() => setRailPopover(railPopover === 'search' ? null : 'search')}
            className={`${ICON_BTN} ${railPopover === 'search' ? 'bg-[var(--sidebar-hover)] text-[var(--content-primary)]' : ''}`}
            title="Search"
          >
            <Search className="w-4 h-4" />
            {activeContentTagFilter && (
              <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-[var(--accent-primary)]" />
            )}
          </button>
          {railPopover === 'search' && (
            <div ref={railPopoverRef} className={RAIL_POPOVER} style={{ boxShadow: 'var(--shadow-lg)', top: '-4px' }}>
              <div className="p-2">
                <div className="flex flex-wrap items-center gap-1 px-2.5 py-1.5 bg-[var(--surface-secondary)] rounded-lg border border-[var(--border-primary)] min-h-[32px]">
                  <Search className="w-3.5 h-3.5 text-[var(--content-tertiary)] flex-shrink-0" />
                  {activeContentTagFilter && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[var(--accent-subtle,rgba(var(--accent-rgb),0.12))] text-xs text-[var(--accent-primary)] font-normal flex-shrink-0">
                      <Tag className="w-2.5 h-2.5" />
                      {activeContentTagFilter.name}
                      <button
                        onMouseDown={(e) => { e.preventDefault(); onContentTagFilterChange?.(null); }}
                        className="hover:text-[var(--content-primary)] ml-0.5"
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  )}
                  <input
                    ref={railSearchInputRef}
                    type="text"
                    value={railSearchQuery}
                    onChange={(e) => { setRailSearchQuery(e.target.value); onSearchChange(e.target.value); }}
                    placeholder={activeContentTagFilter ? '' : 'Search events...'}
                    className="flex-1 min-w-[60px] text-sm bg-transparent outline-none focus:outline-none focus-visible:outline-none text-[var(--content-primary)] placeholder-[var(--content-tertiary)] placeholder:font-light"
                  />
                  {railSearchQuery && (
                    <button onClick={() => { setRailSearchQuery(''); onSearchChange(''); }} className="text-[var(--content-tertiary)] hover:text-[var(--content-primary)]" aria-label="Clear search">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {/* Tag suggestions in rail search */}
                {railSearchQuery.trim() && contentTags.filter(t => t.name.includes(railSearchQuery.trim().toLowerCase())).length > 0 && (
                  <div className="mt-1 border-t border-[var(--border-secondary)] pt-1">
                    {contentTags.filter(t => t.name.includes(railSearchQuery.trim().toLowerCase())).slice(0, 4).map(tag => (
                      <button
                        key={tag.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          onContentTagFilterChange?.(tag);
                          setRailSearchQuery('');
                          onSearchChange('');
                          setRailPopover(null);
                        }}
                        className="w-full px-2 py-1.5 text-sm text-left flex items-center gap-2 hover:bg-[var(--sidebar-hover)] rounded-lg transition-colors"
                      >
                        <Tag className="w-3 h-3 text-[var(--accent-primary)] flex-shrink-0" />
                        <span className="text-[var(--accent-primary)]">{tag.name}</span>
                        <span className="text-xs text-[var(--content-tertiary)] ml-auto">Filter</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Categories */}
        <div className="relative">
          <button
            onClick={() => setRailPopover(railPopover === 'categories' ? null : 'categories')}
            className={`${ICON_BTN} ${railPopover === 'categories' ? 'bg-[var(--sidebar-hover)] text-[var(--content-primary)]' : ''}`}
            title="Calendars"
          >
            <Tag className="w-4 h-4" />
          </button>
          {railPopover === 'categories' && (
            <div ref={railPopoverRef} className={RAIL_POPOVER} style={{ boxShadow: 'var(--shadow-lg)', top: '-4px' }}>
              <div className="px-3 py-2 border-b border-[var(--border-primary)] flex items-center justify-between">
                <span className="text-xs font-medium text-[var(--content-secondary)]">Calendars</span>
                <button
                  onClick={() => allTagsSelected ? onDeselectAllTags() : onSelectAllTags()}
                  className="text-[10px] text-[var(--accent-primary)] hover:underline"
                >
                  {allTagsSelected ? 'Hide all' : 'Show all'}
                </button>
              </div>
              <div className="p-1.5 max-h-64 overflow-y-auto scrollbar-thin">
                {displayTags.map(tag => {
                  const isSelected = effectiveFilters.has(tag.id);
                  return (
                    <button
                      key={tag.id}
                      onClick={() => onToggleCalendarFilter(tag.id)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors"
                    >
                      <div
                        className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isHexColor(tag.color) ? '' : tag.color} ${isSelected ? '' : 'opacity-40'}`}
                        style={isHexColor(tag.color) ? { backgroundColor: tag.color } : undefined}
                      />
                      <span className={`flex-1 text-xs text-left truncate ${isSelected ? 'text-[var(--content-primary)]' : 'text-[var(--content-tertiary)]'}`}>
                        {tag.name}
                      </span>
                      {isSelected && <Check className="w-3 h-3 text-[var(--accent-primary)]" />}
                    </button>
                  );
                })}
                {hasUnassignedEvents && (
                  <button
                    onClick={() => onToggleCalendarFilter(UNASSIGNED_CALENDAR_ID)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors"
                  >
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 bg-unassigned ${effectiveFilters.has(UNASSIGNED_CALENDAR_ID) ? '' : 'opacity-40'}`} />
                    <span className={`flex-1 text-xs text-left italic truncate ${effectiveFilters.has(UNASSIGNED_CALENDAR_ID) ? 'text-[var(--content-primary)]' : 'text-[var(--content-tertiary)]'}`}>
                      Unassigned
                    </span>
                    {effectiveFilters.has(UNASSIGNED_CALENDAR_ID) && <Check className="w-3 h-3 text-[var(--accent-primary)]" />}
                  </button>
                )}
                {displayTags.length === 0 && !hasUnassignedEvents && (
                  <p className="text-xs text-[var(--content-tertiary)] py-2 px-1.5">No calendars yet</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Calendars */}
        <div className="relative">
          <button
            onClick={() => setRailPopover(railPopover === 'calendars' ? null : 'calendars')}
            className={`${ICON_BTN} ${railPopover === 'calendars' ? 'bg-[var(--sidebar-hover)] text-[var(--content-primary)]' : ''}`}
            title="Other calendars"
          >
            <Calendar className="w-4 h-4" />
          </button>
          {railPopover === 'calendars' && (
            <div ref={railPopoverRef} className={RAIL_POPOVER} style={{ boxShadow: 'var(--shadow-lg)', top: '-4px' }}>
              <div className="px-3 py-2 border-b border-[var(--border-primary)] flex items-center justify-between">
                <span className="text-xs font-medium text-[var(--content-secondary)]">Other calendars</span>
                <button
                  onClick={onShowAddCalendarModal}
                  className="text-[var(--content-tertiary)] hover:text-[var(--accent-primary)] transition-colors"
                  title="Add calendar"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="p-1.5 max-h-64 overflow-y-auto scrollbar-thin">
                {importedCalendars.length === 0 ? (
                  <p className="text-xs text-[var(--content-tertiary)] py-2 px-1.5">No calendars linked yet</p>
                ) : (
                  importedCalendars.map(calendar => (
                    <button
                      key={calendar.id}
                      onClick={() => onToggleCalendar(calendar.id)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors"
                    >
                      <div
                        className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isHexColor(calendar.color) ? '' : calendar.color} ${calendar.enabled ? '' : 'opacity-40'}`}
                        style={isHexColor(calendar.color) ? { backgroundColor: calendar.color } : undefined}
                      />
                      <span className={`flex-1 text-xs text-left truncate ${calendar.enabled ? 'text-[var(--content-primary)]' : 'text-[var(--content-tertiary)]'}`}>
                        {calendar.name}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Tasks */}
        <div className="relative">
          <button
            onClick={() => setRailPopover(railPopover === 'tasks' ? null : 'tasks')}
            className={`${ICON_BTN} ${railPopover === 'tasks' ? 'bg-[var(--sidebar-hover)] text-[var(--content-primary)]' : ''}`}
            title="Tasks"
          >
            <ListChecks className="w-4 h-4" />
          </button>
          {railPopover === 'tasks' && (
            <div ref={railPopoverRef} className={RAIL_POPOVER} style={{ boxShadow: 'var(--shadow-lg)', top: '-4px', width: '240px' }}>
              <div className="px-3 py-2 border-b border-[var(--border-primary)] flex items-center justify-between">
                <span className="text-xs font-medium text-[var(--content-secondary)]">Tasks</span>
                <button
                  onClick={() => { setQuickAdding(true); setTimeout(() => quickAddInputRef.current?.focus(), 50); }}
                  className="text-[var(--content-tertiary)] hover:text-[var(--accent-primary)] transition-colors"
                  title="Quick add task"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="p-1.5 max-h-72 overflow-y-auto scrollbar-thin">
                {/* Quick add in rail popover */}
                {quickAdding && (
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (!quickAddTitle.trim() || !onQuickAddTodo) return;
                      await onQuickAddTodo(quickAddTitle.trim());
                      setQuickAddTitle('');
                      quickAddInputRef.current?.focus();
                    }}
                    className="px-1 pb-1.5"
                  >
                    <div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-[var(--surface-secondary)] border border-[var(--border-primary)] focus-within:border-[var(--accent-primary)] transition-colors">
                      <Circle className="w-3.5 h-3.5 text-[var(--content-tertiary)] flex-shrink-0" />
                      <input
                        ref={quickAddInputRef}
                        type="text"
                        value={quickAddTitle}
                        onChange={(e) => setQuickAddTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') { setQuickAddTitle(''); setQuickAdding(false); } }}
                        placeholder="Add a task..."
                        className="flex-1 text-xs bg-transparent outline-none focus:outline-none focus-visible:outline-none text-[var(--content-primary)] placeholder-[var(--content-tertiary)] min-w-0"
                      />
                    </div>
                  </form>
                )}
                {sortedTasks.length === 0 && !quickAdding ? (
                  <p className="text-xs text-[var(--content-tertiary)] py-2 px-1.5">No tasks yet</p>
                ) : (
                  groupedTasks.map(({ label, tasks }) => (
                    <div key={label}>
                      <div className="text-[10px] font-medium text-[var(--content-tertiary)] uppercase tracking-wider px-2 pt-2 pb-1">{label}</div>
                      {tasks.map(task => {
                        const taskColor = getTaskColor(task);
                        return (
                          <div
                            key={task.id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[var(--sidebar-hover)] cursor-pointer"
                            onClick={() => { setRailPopover(null); onOpenTodo(task.id); }}
                          >
                            <button
                              onClick={(e) => { e.stopPropagation(); onToggleTodo(task.id); }}
                              className={`flex-shrink-0 transition-colors ${taskColor ? '' : 'text-[var(--content-tertiary)] hover:text-[var(--accent-primary)]'}`}
                              style={taskColor ? { color: taskColor } : undefined}
                            >
                              {isTaskDone(task) ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                            </button>
                            <span className={`flex-1 text-xs font-light truncate ${isTaskDone(task) ? 'line-through italic text-[var(--content-tertiary)]' : 'text-[var(--content-primary)]'}`}>
                              {task.title}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex-1" />

        {/* Feedback */}
        <button onClick={() => (window as any).uj?.showWidget()} className={ICON_BTN} title="Send Feedback">
          <MessageSquare className="w-4 h-4" />
        </button>

        {/* Settings */}
        <button onClick={onOpenSpaceSettings} className={ICON_BTN} title="Settings">
          <Settings className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // ─── EXPANDED SIDEBAR ─────────────────────────────────────────────
  return (
    <div className="h-full bg-[var(--sidebar-bg)] flex-shrink-0 flex flex-col overflow-hidden" style={{ width: '224px' }}>

      {/* Row 1: App name + collapse */}
      <div className="flex items-center justify-between px-4 h-10 mb-2 flex-shrink-0">
        <Logo size="sm" href="/" />
        <button
          onClick={onToggleCollapse}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[var(--sidebar-hover)] text-[var(--content-tertiary)] hover:text-[var(--content-primary)] transition-colors flex-shrink-0"
          title="Collapse sidebar"
        >
          <ChevronsLeft className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Row 2: Space selector (full-width) */}
      <div className="px-1.5 pb-2 flex-shrink-0" ref={mainMenuRef}>
        <button
          onClick={() => setMainMenuOpen(!mainMenuOpen)}
          className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-[var(--sidebar-hover)] rounded-lg transition-colors"
        >
          <span className="flex-1 text-left text-xs text-[var(--content-secondary)] truncate">
            {currentSpaceName}
          </span>
          <ChevronDown className={`w-3.5 h-3.5 text-[var(--content-tertiary)] transition-transform flex-shrink-0 ${mainMenuOpen ? 'rotate-180' : ''}`} />
        </button>

        {mainMenuOpen && (
          <div className="absolute left-3 right-3 mt-1 bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-lg z-50 py-1 animate-slideDown" style={{ width: '200px', boxShadow: 'var(--shadow-lg)' }}>
            <button
              onClick={() => { setCurrentSpace(null); setMainMenuOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[var(--sidebar-hover)] transition-colors ${isPersonalSpace ? 'bg-[var(--accent-subtle)]' : ''}`}
            >
              <span className="flex-1 text-left text-xs text-[var(--content-primary)] truncate">{personalSpaceName}</span>
              {isPersonalSpace && <Check className="w-3.5 h-3.5 text-[var(--accent-primary)]" />}
            </button>
            {spacesLoading ? (
              <div className="px-3 py-1.5 text-xs text-[var(--content-tertiary)]">Loading...</div>
            ) : (
              spaces.map(space => (
                <button
                  key={space.id}
                  onClick={() => { setCurrentSpace(space); setMainMenuOpen(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[var(--sidebar-hover)] transition-colors ${currentSpace?.id === space.id ? 'bg-[var(--accent-subtle)]' : ''}`}
                >
                  <span className="flex-1 text-left text-xs text-[var(--content-primary)] truncate">{space.name}</span>
                  {currentSpace?.id === space.id && <Check className="w-3.5 h-3.5 text-[var(--accent-primary)]" />}
                </button>
              ))
            )}
            <div className="border-t border-[var(--border-secondary)] my-1" />
            <button
              onClick={() => { onOpenSpaceSettings(); setMainMenuOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[var(--sidebar-hover)] transition-colors text-[var(--content-primary)]"
            >
              <Settings className="w-3.5 h-3.5" />
              <span className="text-xs">Settings</span>
            </button>
            <div className="border-t border-[var(--border-secondary)] my-1" />
            <button
              disabled
              className="w-full flex items-center gap-2 px-3 py-1.5 transition-colors text-[var(--content-tertiary)] cursor-not-allowed opacity-50"
            >
              <HelpCircle className="w-3.5 h-3.5" />
              <span className="text-xs">Help & Support</span>
            </button>
            <div className="border-t border-[var(--border-secondary)] my-1" />
            <div className="px-3 py-1.5">
              <div className="text-[10px] text-[var(--content-tertiary)] uppercase tracking-wider">Logged in as</div>
              <div className="text-xs text-[var(--content-primary)] truncate mt-0.5">{user?.email || 'Not signed in'}</div>
            </div>
            <div className="border-t border-[var(--border-secondary)] my-1" />
            <button
              onClick={() => { onSignOut(); setMainMenuOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[var(--sidebar-hover)] transition-colors text-[var(--content-secondary)]"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="text-xs">Sign Out</span>
            </button>
          </div>
        )}
      </div>

      {/* Search bar */}
      <div className="px-1.5 pb-3 flex-shrink-0 relative">
        <div className="flex flex-wrap items-center gap-1 px-2.5 py-1.5 bg-[var(--surface-tertiary)] rounded-lg border border-[var(--border-primary)] min-h-[32px]">
          <Search className="w-3.5 h-3.5 text-[var(--content-tertiary)] flex-shrink-0" />
          {/* Active tag filter chip */}
          {activeContentTagFilter && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[var(--accent-subtle,rgba(var(--accent-rgb),0.12))] text-xs text-[var(--accent-primary)] font-normal flex-shrink-0">
              <Tag className="w-2.5 h-2.5" />
              {activeContentTagFilter.name}
              <button
                onMouseDown={(e) => { e.preventDefault(); onContentTagFilterChange?.(null); }}
                className="hover:text-[var(--content-primary)] ml-0.5"
                aria-label="Clear tag filter"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </span>
          )}
          <input
            ref={searchInputRef}
            data-sidebar-search
            type="text"
            value={searchQuery}
            onChange={(e) => { setTagSuggestionsOpen(true); onSearchChange(e.target.value); }}
            onFocus={() => setTagSuggestionsOpen(true)}
            onBlur={() => setTimeout(() => setTagSuggestionsOpen(false), 150)}
            placeholder={activeContentTagFilter ? '' : 'Search...'}
            className="flex-1 min-w-[60px] text-sm bg-transparent outline-none focus:outline-none focus-visible:outline-none text-[var(--content-primary)] placeholder-[var(--content-tertiary)] placeholder:font-light"
          />
          {searchQuery && (
            <button onClick={() => onSearchChange('')} className="text-[var(--content-tertiary)] hover:text-[var(--content-primary)]" aria-label="Clear search">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {/* Tag suggestions dropdown */}
        {tagSuggestionsOpen && tagSuggestions.length > 0 && (
          <div className="absolute left-1.5 right-1.5 bottom-full mb-1 bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-xl py-1 z-50" style={{ boxShadow: 'var(--shadow-lg)' }}>
            {tagSuggestions.map(tag => (
              <button
                key={tag.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onContentTagFilterChange?.(tag);
                  onSearchChange('');
                  setTagSuggestionsOpen(false);
                }}
                className="w-full px-3 py-2 text-sm text-left flex items-center gap-2 hover:bg-[var(--sidebar-hover)] transition-colors"
              >
                <Tag className="w-3 h-3 text-[var(--accent-primary)] flex-shrink-0" />
                <span className="text-[var(--accent-primary)]">{tag.name}</span>
                <span className="text-xs text-[var(--content-tertiary)] ml-auto">Filter by tag</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin px-1.5 pb-2 space-y-1">

        {/* ── CATEGORIES ── */}
        <div>
          <div className="flex items-center gap-2 px-1.5 py-1.5">
            <span className="text-sm font-medium text-[var(--content-primary)] flex-1">Calendars</span>
            <button
              onClick={() => allTagsSelected ? onDeselectAllTags() : onSelectAllTags()}
              className="p-0.5 text-[var(--content-tertiary)] hover:text-[var(--content-secondary)] hover:bg-[var(--sidebar-hover)] rounded transition-colors"
              title={allTagsSelected ? 'Hide all' : 'Show all'}
            >
              {allTagsSelected ? <CheckSquare className="w-3.5 h-3.5" /> : noTagsSelected ? <Square className="w-3.5 h-3.5" /> : (
                <div className="w-3.5 h-3.5 relative">
                  <Square className="w-3.5 h-3.5" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-1.5 h-1.5 bg-[var(--content-tertiary)] rounded-sm" />
                  </div>
                </div>
              )}
            </button>
            <button
              onClick={(e) => onOpenCategoryMenu({ name: '', color: COLORS[Math.floor(Math.random() * COLORS.length)].bg }, e.currentTarget, true)}
              className="p-0.5 text-[var(--content-tertiary)] hover:text-[var(--accent-primary)] hover:bg-[var(--sidebar-hover)] rounded transition-colors"
              title="Add category"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => toggleSection('categories')}
              className="p-0.5 text-[var(--content-tertiary)] hover:text-[var(--content-secondary)] hover:bg-[var(--sidebar-hover)] rounded transition-colors"
              aria-label={collapsedSections.has('categories') ? 'Expand categories' : 'Collapse categories'}
            >
              {collapsedSections.has('categories')
                ? <ChevronLeft className="w-3 h-3" />
                : <ChevronDown className="w-3 h-3" />
              }
            </button>
          </div>

          {!collapsedSections.has('categories') && (
            <div className="space-y-0.5">
              {displayTags.map(tag => {
                const isSelected = effectiveFilters.has(tag.id);
                const count = tagEventCounts[tag.id] ?? 0;
                const percent = totalYearEvents > 0 ? Math.round((count / totalYearEvents) * 100) : 0;

                return (
                  <div
                    key={tag.id}
                    className="relative flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-[var(--sidebar-hover)] group cursor-pointer"
                    onClick={() => onToggleCalendarFilter(tag.id)}
                  >
                    <div
                      className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${!isDark && !isHexColor(tag.color) ? tag.color : ''} ${isSelected ? '' : 'opacity-40'}`}
                      style={isDark
                        ? { background: getTonalColors(tag.color).textDark }
                        : isHexColor(tag.color) ? { backgroundColor: tag.color } : undefined}
                    />
                    <span className={`flex-1 text-xs font-light truncate ${isSelected ? 'text-[var(--content-primary)]' : 'text-[var(--content-tertiary)]'}`}>
                      {tag.name}
                      {tag.is_shared && <Share2 className="inline w-3 h-3 ml-1 text-[var(--accent-primary)]" />}
                    </span>
                    <div className="absolute right-0 bottom-full mb-1 hidden group-hover:block z-50">
                      <div className="bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-md px-2 py-0.5 text-[10px] text-[var(--content-primary)] whitespace-nowrap">
                        {count} of {totalYearEvents} ({percent}%)
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenCategoryMenu({ id: tag.id, name: tag.name, color: tag.color, is_shared: tag.is_shared, share_token: tag.share_token }, e.currentTarget, false);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-[var(--surface-tertiary)] rounded text-[var(--content-secondary)] transition-opacity flex-shrink-0"
                      aria-label={`Edit ${tag.name} category`}
                    >
                      <MoreVertical className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}

              {hasUnassignedEvents && (() => {
                const isSelected = effectiveFilters.has(UNASSIGNED_CALENDAR_ID);
                const count = unassignedEvents.length;
                const percent = totalYearEvents > 0 ? Math.round((count / totalYearEvents) * 100) : 0;
                return (
                  <div
                    className="relative flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-[var(--sidebar-hover)] group cursor-pointer"
                    onClick={() => onToggleCalendarFilter(UNASSIGNED_CALENDAR_ID)}
                  >
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 bg-unassigned ${isSelected ? '' : 'opacity-40'}`} />
                    <span className={`flex-1 text-xs font-light italic truncate ${isSelected ? 'text-[var(--content-primary)]' : 'text-[var(--content-tertiary)]'}`}>
                      Unassigned
                    </span>
                    <div className="absolute right-0 bottom-full mb-1 hidden group-hover:block z-50">
                      <div className="bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-md px-2 py-0.5 text-[10px] text-[var(--content-primary)] whitespace-nowrap">
                        {count} of {totalYearEvents} ({percent}%)
                      </div>
                    </div>
                    {/* Spacer matching the MoreVertical button in regular category rows */}
                    <div className="w-4 h-4 flex-shrink-0" />
                  </div>
                );
              })()}

              {displayTags.length === 0 && !hasUnassignedEvents && (
                <div className="flex flex-col items-center text-center gap-2 py-4 px-2">
                  <div>
                    <p className="text-xs font-normal text-[var(--content-secondary)]">No calendars yet</p>
                    <p className="text-[10px] text-[var(--content-tertiary)] mt-0.5">Add one to colour-code your events</p>
                  </div>
                  <button
                    onClick={(e) => onOpenCategoryMenu({ name: '', color: COLORS[Math.floor(Math.random() * COLORS.length)].bg }, e.currentTarget, true)}
                    className="text-[10px] font-medium text-[var(--accent-primary)] hover:underline transition-colors"
                  >
                    + New calendar
                  </button>
                </div>
              )}

            </div>
          )}
        </div>

        {/* ── CALENDARS ── */}
        <div>
          <div className="flex items-center gap-2 px-1.5 py-1.5">
            <span className="text-sm font-medium text-[var(--content-primary)] flex-1">Other calendars</span>
            {importedCalendars.length > 0 && (
              <button
                onClick={() => {
                  const allEnabled = importedCalendars.every(c => c.enabled);
                  for (const calendar of importedCalendars) {
                    if (allEnabled ? calendar.enabled : !calendar.enabled) {
                      onToggleCalendar(calendar.id);
                    }
                  }
                }}
                className="p-0.5 text-[var(--content-tertiary)] hover:text-[var(--content-secondary)] hover:bg-[var(--sidebar-hover)] rounded transition-colors"
                title={importedCalendars.every(c => c.enabled) ? 'Hide all' : 'Show all'}
              >
                {importedCalendars.every(c => c.enabled) ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
              </button>
            )}
            <button onClick={onShowAddCalendarModal} className="p-0.5 text-[var(--content-tertiary)] hover:text-[var(--accent-primary)] hover:bg-[var(--sidebar-hover)] rounded transition-colors" title="Add calendar">
              <Plus className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setCalendarsCollapsed(!calendarsCollapsed)}
              className="p-0.5 text-[var(--content-tertiary)] hover:text-[var(--content-secondary)] hover:bg-[var(--sidebar-hover)] rounded transition-colors"
              aria-label={calendarsCollapsed ? 'Expand calendars' : 'Collapse calendars'}
            >
              {calendarsCollapsed
                ? <ChevronLeft className="w-3 h-3" />
                : <ChevronDown className="w-3 h-3" />
              }
            </button>
          </div>

          {!calendarsCollapsed && (
            <div className="space-y-0.5">
              {importedCalendars.length === 0 ? (
                <div className="flex flex-col items-center text-center gap-2 py-4 px-2">
                  <div>
                    <p className="text-xs font-normal text-[var(--content-secondary)]">No calendars linked</p>
                    <p className="text-[10px] text-[var(--content-tertiary)] mt-0.5">Import from Google or iCal</p>
                  </div>
                  <button
                    onClick={onShowAddCalendarModal}
                    className="text-[10px] font-medium text-[var(--accent-primary)] hover:underline transition-colors"
                  >
                    + Link calendar
                  </button>
                </div>
              ) : (
                importedCalendars.map(calendar => {
                  return (
                    <div key={calendar.id} className="flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-[var(--sidebar-hover)] group cursor-pointer" onClick={() => onToggleCalendar(calendar.id)}>
                      <div
                        className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isHexColor(calendar.color) ? '' : calendar.color} ${calendar.enabled ? '' : 'opacity-40'}`}
                        style={isHexColor(calendar.color) ? { backgroundColor: calendar.color } : undefined}
                      />
                      <span className={`flex-1 text-xs font-light truncate ${calendar.enabled ? 'text-[var(--content-primary)]' : 'text-[var(--content-tertiary)]'}`}>{calendar.name}</span>
                      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity">
                        {calendar.url !== 'file://imported' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onSyncCalendar(calendar.id); }}
                            disabled={syncing === calendar.id}
                            className="p-1 hover:bg-[var(--surface-tertiary)] rounded text-[var(--content-secondary)]"
                            title="Sync now"
                          >
                            {syncing === calendar.id ? (
                              <div className="w-3.5 h-3.5 border-2 border-[var(--content-tertiary)] border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            )}
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); onOpenCalendarMenu({ id: calendar.id, name: calendar.name, url: calendar.url, color: calendar.color }, e.currentTarget); }}
                          className="p-1 hover:bg-[var(--sidebar-hover)] rounded text-[var(--content-tertiary)]"
                          title="Edit calendar"
                        >
                          <MoreVertical className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
              {importedCalendars.some(c => c.sync_error && c.url !== 'file://imported') && (
                <p className="text-xs text-[var(--error)] mt-1 px-1.5">Some calendars failed to sync</p>
              )}
            </div>
          )}
        </div>

        {/* ── TASKS ── */}
        <div>
          <div className="flex items-center gap-2 px-1.5 py-1.5">
            <span className="text-sm font-medium text-[var(--content-primary)] flex-1">Tasks</span>
            <button
              onClick={() => { setQuickAdding(true); setTasksCollapsed(false); setTimeout(() => quickAddInputRef.current?.focus(), 50); }}
              className="p-0.5 text-[var(--content-tertiary)] hover:text-[var(--accent-primary)] hover:bg-[var(--sidebar-hover)] rounded transition-colors"
              title="Quick add task"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setTasksCollapsed(!tasksCollapsed)}
              className="p-0.5 text-[var(--content-tertiary)] hover:text-[var(--content-secondary)] hover:bg-[var(--sidebar-hover)] rounded transition-colors"
              aria-label={tasksCollapsed ? 'Expand tasks' : 'Collapse tasks'}
            >
              {tasksCollapsed
                ? <ChevronLeft className="w-3 h-3" />
                : <ChevronDown className="w-3 h-3" />
              }
            </button>
          </div>

          {!tasksCollapsed && (
            <div className="space-y-3">
              {/* Quick add input */}
              {quickAdding && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!quickAddTitle.trim() || !onQuickAddTodo) return;
                    await onQuickAddTodo(quickAddTitle.trim());
                    setQuickAddTitle('');
                    setQuickAdding(false);
                  }}
                  className="px-1.5"
                >
                  <div className="flex items-center gap-2 px-1.5 py-1 rounded-lg bg-[var(--surface-primary)] border border-[var(--border-primary)] focus-within:border-[var(--accent-primary)] transition-colors">
                    <Circle className="w-3.5 h-3.5 text-[var(--content-tertiary)] flex-shrink-0" />
                    <input
                      ref={quickAddInputRef}
                      type="text"
                      value={quickAddTitle}
                      onChange={(e) => setQuickAddTitle(e.target.value)}
                      onBlur={() => { if (!quickAddTitle.trim()) setQuickAdding(false); }}
                      onKeyDown={(e) => { if (e.key === 'Escape') { setQuickAddTitle(''); setQuickAdding(false); } }}
                      placeholder="Add a task..."
                      className="flex-1 text-xs bg-transparent outline-none focus:outline-none focus-visible:outline-none text-[var(--content-primary)] placeholder-[var(--content-tertiary)] min-w-0"
                    />
                  </div>
                </form>
              )}
              {sortedTasks.length === 0 && !quickAdding ? (
                <div className="flex flex-col items-center text-center gap-2 py-4 px-2">
                  <div>
                    <p className="text-xs font-normal text-[var(--content-secondary)]">No tasks yet</p>
                    <p className="text-[10px] text-[var(--content-tertiary)] mt-0.5">Add one to get started</p>
                  </div>
                  <button
                    onClick={() => { setQuickAdding(true); setTimeout(() => quickAddInputRef.current?.focus(), 50); }}
                    className="text-[10px] font-medium text-[var(--accent-primary)] hover:underline transition-colors"
                  >
                    + New task
                  </button>
                </div>
              ) : (
                groupedTasks.map(({ label, tasks }) => (
                  <div key={label}>
                    <div className="text-[10px] font-medium text-[var(--content-tertiary)] uppercase tracking-wider px-1.5 mb-1">{label}</div>
                    <div className="space-y-0.5">
                      {tasks.map(task => {
                        const taskColor = getTaskColor(task);
                        return (
                        <div
                          key={task.id}
                          className="flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-[var(--sidebar-hover)] group"
                        >
                          <button
                            onClick={() => onToggleTodo(task.id)}
                            className={`flex-shrink-0 transition-colors ${taskColor ? '' : 'text-[var(--content-tertiary)] hover:text-[var(--accent-primary)]'}`}
                            style={taskColor ? { color: taskColor } : undefined}
                            title={isTaskDone(task) ? 'Mark incomplete' : 'Mark complete'}
                          >
                            {isTaskDone(task) ? (
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            ) : (
                              <Circle className="w-3.5 h-3.5" />
                            )}
                          </button>
                          <span className={`flex-1 text-xs font-light truncate ${
                            isTaskDone(task)
                              ? 'line-through italic text-[var(--content-tertiary)]'
                              : 'text-[var(--content-primary)]'
                          }`}>
                            {task.title}
                          </span>
                          <button
                            onClick={() => onOpenTodo(task.id)}
                            className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-[var(--surface-tertiary)] rounded text-[var(--content-secondary)] transition-opacity flex-shrink-0"
                            title="Edit task"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
              {hasMoreTasks && (
                <button
                  onClick={() => setTasksVisible(v => v + TASKS_PAGE_SIZE)}
                  className="w-full text-xs text-[var(--accent-primary)] hover:text-[var(--accent-hover)] py-1 text-center transition-colors"
                >
                  Load more
                </button>
              )}
            </div>
          )}
        </div>

        {/* Space Members */}
        <div className="pt-1">
          <SpaceMembers />
        </div>
      </div>

      {/* Sticky bottom: Feedback & Settings */}
      <div className="flex-shrink-0 px-1.5 pb-3 pt-2">
        <div className="border-t border-[var(--border-primary)] mb-1 mx-1.5" />
        <button
          onClick={() => (window as any).uj?.showWidget()}
          className="w-full flex items-center gap-2 px-1.5 py-2 rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors"
        >
          <MessageSquare className="w-3.5 h-3.5 text-[var(--content-tertiary)] flex-shrink-0" />
          <span className="text-xs text-[var(--content-secondary)]">Send Feedback</span>
        </button>
        <button
          onClick={onOpenSpaceSettings}
          className="w-full flex items-center gap-2 px-1.5 py-2 rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors"
        >
          <Settings className="w-3.5 h-3.5 text-[var(--content-tertiary)] flex-shrink-0" />
          <span className="text-xs text-[var(--content-secondary)]">Settings</span>
        </button>
      </div>

    </div>
  );
};
